import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { listProductNdcsApi, setPartnerNdcPreferenceApi } from 'api/inventory';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { capitalizeName } from 'common/utils/titleCase';
import { DIALOG_MODE } from '../constants';
import { catalogName, compareAlphabetically } from '../utils/formatters';
import PreferenceContextPanel from './PreferenceContextPanel';
import PreferenceDrugAutocomplete from './PreferenceDrugAutocomplete';

const EMPTY_ARRAY = [];

const TITLE_BY_MODE = {
  [DIALOG_MODE.ADD_ANY]: 'Add partner preference',
  [DIALOG_MODE.ADD_FOR_PARTNER]: 'Add drug preference',
  [DIALOG_MODE.CHANGE]: 'Change preferred NDC',
};

const SetPartnerNdcPreferenceDialog = ({
  open,
  mode,
  partner,
  preference,
  preferencesByPartnerId,
  onNeedPartnerPreferences,
  onClose,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const currentTenantId = useSelector(({ auth }) => auth.currentTenantId);
  const partners = useSelector(({ partner: slice }) => slice[currentTenantId] ?? EMPTY_ARRAY);

  const partnerLocked = mode !== DIALOG_MODE.ADD_ANY;
  const drugLocked = mode === DIALOG_MODE.CHANGE;

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [ndcOptions, setNdcOptions] = useState([]);
  const [selectedNdc, setSelectedNdc] = useState('');

  const [loadingNdcs, setLoadingNdcs] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // Partners this dialog has already asked for. Without it a failed children fetch
  // never populates the cache, so the effect below would see a permanent miss and
  // re-request on every render.
  const requestedPartnerIdsRef = useRef(new Set());

  const retryLoad = useCallback(() => setReloadToken((token) => token + 1), []);

  // Cased here rather than at the api boundary: getPartners is shared with other screens.
  const activePartners = useMemo(
    () =>
      partners
        .filter((item) => item.enabled && !item.isSuspended)
        .map((item) => ({ ...item, partnerName: capitalizeName(item.partnerName) }))
        .sort(compareAlphabetically((item) => item.partnerName)),
    [partners]
  );

  // Seed from the launch context every time the dialog opens, so a previous
  // session's picks never leak into the next one.
  useEffect(() => {
    if (!open) return;

    const seededPartner = partner
      ? { partnerUuid: partner.partnerId, partnerName: partner.partnerName }
      : null;

    setSelectedPartner(seededPartner);
    setSelectedDrug(
      preference
        ? {
            productId: preference.productId,
            drugName: preference.drugName,
            strength: preference.strength,
            dosageForm: preference.dosageForm,
            formAlias: preference.formAlias,
            packSizeValue: preference.packSizeValue,
            packSizeUnit: preference.packSizeUnit,
          }
        : null
    );
    setSelectedNdc(preference?.ndc ?? '');
    setNdcOptions([]);
    setLoadError('');
    setSaveError('');
  }, [open, partner, preference]);

  const selectedPartnerId = selectedPartner?.partnerUuid;

  // The screen caches each partner's preferences for its expanded row, so the
  // exclusion list is usually already in hand. When it is not — the toolbar route
  // can pick a partner whose row was never opened — the same fetch runs and lands
  // in the same cache, so expanding that row later costs nothing.
  const cachedPreferences = selectedPartnerId
    ? preferencesByPartnerId[selectedPartnerId]
    : undefined;

  useEffect(() => {
    if (!open || !selectedPartnerId || cachedPreferences) return;
    if (requestedPartnerIdsRef.current.has(selectedPartnerId)) return;
    requestedPartnerIdsRef.current.add(selectedPartnerId);
    onNeedPartnerPreferences?.(selectedPartnerId);
  }, [open, selectedPartnerId, cachedPreferences, onNeedPartnerPreferences]);

  const takenProductIds = useMemo(
    () => (cachedPreferences ?? []).map((item) => item.productId),
    [cachedPreferences]
  );

  const productId = selectedDrug?.productId;

  // Belt to the picker's braces. The picker hides what the partner already holds,
  // but a stale list or a concurrent edit could still land here — and the mutation
  // is an upsert, so saving would quietly replace the existing preference rather
  // than fail. Say so instead.
  const isDuplicate =
    mode !== DIALOG_MODE.CHANGE && Boolean(productId) && takenProductIds.includes(productId);

  // NDC options belong to the product, so they are fetched on every drug change
  // rather than shipped with the row. Guarded against a stale response landing
  // after the drug changed or the dialog closed.
  useEffect(() => {
    if (!open || !productId) {
      setNdcOptions([]);
      return undefined;
    }

    let cancelled = false;
    setLoadingNdcs(true);
    setLoadError('');

    listProductNdcsApi(productId)
      .then((options) => {
        if (cancelled) return;
        setNdcOptions(options ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        // Keep the dialog open with a retry instead of closing on a failed read —
        // the same no-silent-failure handling the default-NDC dialog uses.
        setLoadError(getGraphQLErrorMessage(error));
        setNdcOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingNdcs(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, productId, reloadToken]);

  const currentNdc = preference?.ndc ?? '';

  // The saved NDC's manufacturer. Prefer the freshly loaded option so a rename in
  // inventory shows through; fall back to what the row carried when the list has
  // not arrived yet, or when the NDC is no longer one of the product's options.
  const currentManufacturer =
    ndcOptions.find((option) => option.ndc === currentNdc)?.manufacturer ??
    preference?.manufacturer ??
    '';

  // The preferred NDC can stop being associated with the product. When that
  // happens no option carries the "current" marker, so say it explicitly —
  // otherwise "opened at the current value" looks identical to "opened at
  // nothing", and saving reads as a no-op rather than a repair.
  const currentNdcMissing =
    Boolean(currentNdc) && ndcOptions.length > 0 && !ndcOptions.some((o) => o.ndc === currentNdc);

  const selectedOption = ndcOptions.find((option) => option.ndc === selectedNdc);
  const isDirty = selectedNdc !== currentNdc;
  const canSave =
    Boolean(selectedPartner?.partnerUuid) &&
    Boolean(productId) &&
    Boolean(selectedNdc) &&
    selectedOption?.isActive !== false &&
    !isDuplicate &&
    isDirty &&
    !saving;

  const handleSave = () => {
    setSaving(true);
    setSaveError('');

    setPartnerNdcPreferenceApi({
      partnerUuid: selectedPartner.partnerUuid,
      productId,
      ndc: selectedNdc,
    })
      .then(() => {
        enqueueSnackbar(
          `${selectedPartner.partnerName} is now locked to NDC ${selectedNdc} for ${catalogName(
            selectedDrug
          )}`,
          { variant: 'success' }
        );
        onSaved?.({ partnerUuid: selectedPartner.partnerUuid });
        onClose?.();
      })
      .catch((error) => {
        // Deliberately not routed through handleError: that opens the global
        // error dialog, which would sit on top of this one and hide the message
        // for a failure the user fixes right here (a rejected or inactive NDC).
        setSaveError(getGraphQLErrorMessage(error));
      })
      .finally(() => setSaving(false));
  };

  const renderNdcSection = () => {
    if (!productId) {
      return (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Select a drug to choose its preferred NDC.
        </Typography>
      );
    }

    if (loadingNdcs) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={retryLoad}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      );
    }

    if (ndcOptions.length === 0) {
      return (
        <Alert severity='warning'>
          This product has no NDC in the inventory master, so there is nothing to lock to.
        </Alert>
      );
    }

    return (
      <>
        {currentNdcMissing && (
          <Alert severity='warning' sx={{ mb: 1.5 }}>
            Currently{' '}
            <Box component='span' sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {currentNdc}
            </Box>{' '}
            — no longer associated with this product. Picking an NDC below repairs the preference.
          </Alert>
        )}
        <RadioGroup value={selectedNdc} onChange={(event) => setSelectedNdc(event.target.value)}>
          {ndcOptions.map((option) => {
            const isInactive = option.isActive === false;
            const isSelected = option.ndc === selectedNdc;
            const isCurrent = Boolean(currentNdc) && option.ndc === currentNdc;

            return (
              <FormControlLabel
                key={option.ndc}
                value={option.ndc}
                control={<Radio />}
                // An inactive NDC is shown but not selectable: a hard lock onto a
                // deactivated NDC would send every order to review.
                disabled={saving || isInactive}
                sx={{
                  alignItems: 'flex-start',
                  m: 0,
                  mb: 0.75,
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  // The blue wash carries "this is what you are choosing" on its own;
                  // the chip names it for anyone who cannot rely on the colour.
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
                  '&:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, isSelected ? 0.12 : 0.03),
                  },
                  // The label is the whole row, so it has to take the width for the
                  // chips to sit against the right edge.
                  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}
                      >
                        {/* Labelled because the row carries two codes and a name;
                            without them the eye has to work out which is which. */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            NDC
                          </Typography>
                          <Box
                            component='span'
                            sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13.5 }}
                          >
                            {option.ndc}
                          </Box>
                        </Box>

                        {option.manufacturer && (
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                              MFG
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {option.manufacturer}
                            </Typography>
                          </Box>
                        )}

                        {/* Beside the NDC rather than in the chip column: it qualifies
                            this code, and a chip on the right read as a second status
                            competing with the selection itself. */}
                        {isCurrent && (
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary', fontWeight: 700 }}
                          >
                            (current)
                          </Typography>
                        )}
                      </Box>

                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        {catalogName(option)}
                      </Typography>

                      {isInactive && (
                        <Typography
                          variant='caption'
                          sx={{ color: 'warning.main', fontWeight: 600, display: 'block' }}
                        >
                          Inactive NDC — cannot be used as a preference
                        </Typography>
                      )}
                    </Box>

                    {/* No "Selected" chip: the blue wash and the filled radio already
                        say which row is chosen, and a third marker for the same fact
                        just crowded the row. Product default is the only chip left —
                        a read-only fact set on the product in Inventory. */}
                    {option.isDefault && (
                      <Tooltip
                        arrow
                        placement='left'
                        title='Set on the product in Inventory — not editable here'
                      >
                        <Chip
                          size='small'
                          variant='outlined'
                          label='Product default'
                          sx={{ color: 'text.disabled', borderColor: 'divider', flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ fontWeight: 700 }}>{TITLE_BY_MODE[mode]}</DialogTitle>
      <DialogContent dividers>
        {!partnerLocked && (
          <Autocomplete
            size='small'
            fullWidth
            options={activePartners}
            value={selectedPartner}
            onChange={(_, next) => {
              setSelectedPartner(next);
              // Drug options are scoped to the partner, so a partner change
              // invalidates the drug and the NDC beneath it.
              setSelectedDrug(null);
              setSelectedNdc('');
            }}
            isOptionEqualToValue={(option, selected) => option.partnerUuid === selected.partnerUuid}
            getOptionLabel={(option) => option.partnerName ?? ''}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} label='Partner' required />
            )}
            sx={{ mb: 2 }}
          />
        )}

        {!drugLocked && (
          <Box sx={{ mb: 2 }}>
            <PreferenceDrugAutocomplete
              takenProductIds={takenProductIds}
              value={selectedDrug}
              onChange={(next) => {
                setSelectedDrug(next);
                setSelectedNdc('');
              }}
              disabled={saving || !selectedPartnerId}
            />
            {isDuplicate && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                {`${selectedPartner?.partnerName} already has a preference for this drug — use
                  Change on that row instead.`}
              </Alert>
            )}
          </Box>
        )}

        <PreferenceContextPanel
          partner={selectedPartner}
          drug={selectedDrug}
          currentNdc={currentNdc}
          currentManufacturer={currentManufacturer}
        />

        <Typography
          variant='caption'
          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '.06em' }}
        >
          PREFERRED NDC
        </Typography>
        <Box sx={{ mt: 0.5, mb: 2 }}>{renderNdcSection()}</Box>

        {mode === DIALOG_MODE.CHANGE && selectedNdc && selectedNdc !== currentNdc && (
          <Alert severity='info' sx={{ mb: 2 }}>
            <Box component='span' sx={{ fontFamily: 'monospace' }}>
              {currentNdc || '—'}
            </Box>{' '}
            →{' '}
            <Box component='span' sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {selectedNdc}
            </Box>
          </Alert>
        )}

        {saveError && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color='inherit' /> : null}
        >
          Set preference
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SetPartnerNdcPreferenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(Object.values(DIALOG_MODE)).isRequired,
  partner: PropTypes.shape({
    partnerId: PropTypes.string,
    partnerName: PropTypes.string,
  }),
  preference: PropTypes.shape({
    productId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    dosageForm: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    manufacturer: PropTypes.string,
    ndc: PropTypes.string,
  }),
  preferencesByPartnerId: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.shape({ productId: PropTypes.string }))
  ),
  onNeedPartnerPreferences: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

SetPartnerNdcPreferenceDialog.defaultProps = {
  partner: null,
  preference: null,
  preferencesByPartnerId: {},
  onNeedPartnerPreferences: undefined,
};

export default SetPartnerNdcPreferenceDialog;
